import ValidateModule from "./ValidateModule";

import type {
    ILayerMeta,
    LayerInfo,
    SpecialFunction
} from "@esWebAPI/es/model/Layer";
import type {ApiOpts, IEventChannel, ILayerMetaAvailabilities, ITerritoryDependency} from "@esWebAPI/es/model/Models";
import type {Attribute, SortOrder} from "../model/Feature";
import type {Criteria} from "@esWebAPI/es/model/Criteria";
import type AjaxRequestBroker from "../AjaxRequestBroker";
import type CacheModule from "./CacheModule";

export default class LayerMetaModule {
    eventChannel: IEventChannel;
    readonly rqBroker: AjaxRequestBroker;
    readonly resourceName: string;
    readonly cache: CacheModule;
    // We use this virtualSequence for LayerViews created from a LayerMeta
    virtualLayerViewSequence = -1;
    
    constructor(eventChannel: IEventChannel, rqBroker: AjaxRequestBroker, resourceName: string, cache: CacheModule) {
        this.eventChannel = eventChannel;
        this.resourceName = resourceName;
        this.rqBroker = rqBroker;
        this.cache = cache;

        this.eventChannel.addListener("onDestroy", () => {
           this.eventChannel = null; // prevent memory leaks when the map widget is destroyed
        });
    }

    /**
     * @function es.module.[resource].get
     * Get the resource from the server
     * @return a jquery deferred object resolved when the request has completed
     */
    get(id: number, opts?: ApiOpts): JQuery.Deferred<ILayerMeta> {
        if (!ValidateModule.checkId(id)) {
            return es.util.deferred.rejected();
        }
        
        const url = es.api.getURL(this.resourceName) + "/" + id;
        return this.cache.doGet(this.rqBroker, this.resourceName, url, id, opts);
    }

    /**
     * @function es.module.[resource].get
     * @param id layerMeta id
     * Get the resource from the server
     * @param includeAttributeRanges
     * @param opts
     * @return a jquery deferred object resolved when the request has completed
     */
    getInfo(id: number, includeAttributeRanges?: string[], opts?: ApiOpts): JQueryPromise<LayerInfo> {
        if (!ValidateModule.checkId(id)) {
            return es.util.deferred.rejected();
        }
        
        let url = es.api.getURL(this.resourceName) + "/info/" + id;
        if (includeAttributeRanges) {
            url += '?includeAttributeRanges=' + es.util.url.stringify(includeAttributeRanges);
        }
        
        return this.cache.doGet(this.rqBroker, "LayerMeta_LayerInfo", url, id, opts).then((rs: LayerInfo["featureInfo"]) => {
            // Cache doesn't take into account the URL, so we could get an old result from cache that doesn't have the requested attribute ranges
            // so, we need to check if all requested ranges are there, and if not, request again with noCache
            
            opts = opts || {};
            
            if (opts.noCache || !includeAttributeRanges) {
                return rs;
            }
            
            if (es.util.array.includesAll(Object.keys(rs.attributeRanges), includeAttributeRanges)) {
                return rs;
            }
            
            opts.noCache = true;
            return this.cache.doGet(this.rqBroker, "LayerMeta_LayerInfo", url, id, opts);
        });
    }
    
    /**
     * @function es.module.[resource].get
     * Get the resource from the server
     * @param ids
     * @param opts
     * @return a jquery deferred object resolved when the request has completed
     */
    getByIds(ids: number[], opts?: ApiOpts): JQuery.Deferred<ILayerMeta[]> {
        if (!ValidateModule.checkIds(ids)) {
            return es.util.deferred.rejected();
        }
        
        const url = es.api.getURL(this.resourceName) + "/ids?ids=" + es.util.url.stringify(ids);
        return this.cache.doGet(this.rqBroker, this.resourceName, url, ids, opts);
    }

    /**
     * @function es.module.[resource].get
     * Get the resource from the server
     * @param geoMapId
     * @param opts
     * @return a jquery deferred object resolved when the request has completed
     */
    getByGeoMap(geoMapId: number, opts?: ApiOpts): JQuery.Deferred<ILayerMeta[]> {
        if (!ValidateModule.checkId(geoMapId)) {
            return es.util.deferred.rejected();
        }

        const url = es.api.getURL(this.resourceName) + "/get_by_geomap/" + geoMapId; // TODO: this method should be removed and use list?criteria:geomapid=x instead
        return this.cache.doGet(this.rqBroker, "LayerMeta_GeoMap", url, geoMapId, opts);
    }

    /**
     * @function es.module.[resource].get
     * Get the recently used resource from the server
     * @param length how many recent objects are requested
     * @return a jquery deferred object resolved when the request has completed
     */
    getByMostRecent(criteria: Criteria, length: number, usedAfter: boolean, opts?: ApiOpts) {
        opts = opts || {};
        opts.noCache = true;

        return this.cache.doGetList(this.rqBroker, this.resourceName + "/get_by_usage", criteria, undefined, undefined, length, usedAfter, opts);
    }

    /**
     * @function es.module.[resource].get
     * Get the resource from the server
     * @param criteria
     * @param sortOrder
     * @param offset
     * @param length
     * @param usedAfter time in miliseconds to list only objects used after given timestamp, 0 means ignore
     *             the timestamp
     * @return a jquery deferred object resolved when the request has completed
     */
    list(criteria?: Criteria, sortOrder?: SortOrder, offset?: number, length?: number, usedAfter?: boolean, opts?: ApiOpts): JQuery.Deferred<ILayerMeta[]> {
        let url = es.api.getListQueryURL(this.resourceName, criteria, sortOrder, offset, length);
        
        if (url.length > this.rqBroker.MAX_URL_LENGTH) {
            url = es.api.getListQueryURL(this.resourceName + "/list_bulk", null, sortOrder, offset, length);
            return this.rqBroker.doPost({url: url, data : criteria, opts: opts});
        }

        return this.cache.doGetList(this.rqBroker, this.resourceName, criteria, sortOrder, offset, length, usedAfter, opts);
    }

    /**
     * @function es.module.[resource].get
     * Get the resource from the server
     * @param criteria
     * @param sortOrder
     * @param offset
     * @param length
     * @return a jquery deferred object resolved when the request has completed
     */
    listNoSF(criteria: Criteria, sortOrder: SortOrder, offset: number, length: number, opts?: ApiOpts): JQuery.Deferred<ILayerMeta[]> {
        return this.cache.doGetList(this.rqBroker, this.resourceName+"/no_sf_alignment", criteria, sortOrder, offset, length, opts);
    }
    
    /**
     * @function es.module.LayerMeta.createFromLayerView
     * 
     * Creates a new layer meta with copied data from the layerview
     * @param title
     * @param description
     * @param layerViewId
     * @param criteria
     * @return a jquery deferred object resolved when the request has completed
     */
    createFromLayerView(title: string, description: string, layerViewId: number, criteria: Criteria, opts?: ApiOpts): JQueryDeferred<ILayerMeta> {
        if (!ValidateModule.checkMandatoryArg(title) ||
            !ValidateModule.checkId(layerViewId)) {
            return es.util.deferred.rejected();
        }
        
        return this.rqBroker.doPost({
            url: es.api.getURL(this.resourceName) + '/from_layer_view',
            opts: opts,
            data: {
                'layerViewId': layerViewId,
                'title': title,
                'description': description,
                'criteria': criteria
            }
        }).done((rs: any) => { //FIXME :: What type is here?
            this.cache.setEntity(this.resourceName, rs);
            this.cache.clear("Security", "ownership_layermeta");
            
            this.eventChannel.fireEvent("onCreateLayerMeta", rs);
        });
    }
    
    /**
     * @function es.module.[resource].count
     * Get a count of resources matching the given Criteria
     * @param criteria
     * @return long
     */
    count(criteria?: Criteria, opts?: ApiOpts): JQuery.Deferred<number> {
        const url = es.api.getURL(this.resourceName) + "/count" + (criteria ? "?criteria=" + es.util.url.stringify(criteria) : "");
        return this.rqBroker.doGet({url: url, opts: opts});
    }
    
    /**
     * @function es.module.[resource].count
     * Get a count of resources matching the given Criteria
     * @param criteria
     * @return long
     */
    countOwned (criteria?: Criteria, opts?: ApiOpts): JQuery.Deferred<number>{
        const url = es.api.getURL(this.resourceName) + "/count-owned" + (criteria ? "?criteria=" + es.util.url.stringify(criteria) : "");
        return this.rqBroker.doGet({url: url, opts: opts});
    }
    
    /**
     * @function es.module.[resource].put
     * PUT a new resource to the server (note: id attrib must be valid)
     * @param resource e.g. {"$type":"DefaultLayerMeta","labelAttr":"NAME_1","featureSourceId":107,"lastModified":1396367155298,"defaultStyler":{"$type":"SingleStyler","style":{"$type":"PolygonStyle","text":{"weight":1.0,"height":12.0,"strikeThrough":false,"family":"serif","underline":false,"color":4278190080,"italic":false},"fillColor":2160892262,"line":{"color":4278190080,"width":1.0,"endCap":2,"join":0}}},"geomExportable":false,"id":116,"parentId":109,"title":"US States","maxResolution":0.0,"created":1396367155298,"description":"US States","sourceTenantId":2,"minResolution":0.0,"geomType":"POLYGON"}
     * @return a jquery deferred object resolved when the request has completed
     */
    update(resourceObj: ILayerMeta, opts?: ApiOpts): JQuery.Deferred<ILayerMeta> {
        if (!ValidateModule.checkMandatoryArg(resourceObj)) {
            return es.util.deferred.rejected();
        }
        
        return this.rqBroker.doPut({
            url: es.api.getURL(this.resourceName) + "/" + resourceObj.id,
            opts: opts,
            data: resourceObj
        }).done((rs: ILayerMeta) => {
            this.cache.clear("LayerInfo");
            this.cache.clear("LayerMeta");
            this.cache.setEntity(this.resourceName, rs);
            
            this.eventChannel.fireEvent("onUpdateLayerMeta", rs);
        });
    }
    
    /**
     * @function es.module.[resource].samples
     * Get some sample data of the specified LayerMeta IDs
     * TODO: this should only accept layermeta ids. make fn in layerView module that accepts layerViewIds
     * @param ids
     * @param numOfRows Default to 3
     * @param forLayerViews Default to false, which means the first parameter are LayerMeta ids
     * @return a jquery deferred object resolved when the request has completed
     */
    samples(ids: number[], numOfRows?: number, forLayerViews?: number[], opts?: ApiOpts) {
        if (!ValidateModule.checkIds(ids)) {
            return es.util.deferred.rejected();
        }
        
        let url = es.api.getURL(this.resourceName) + "/samples?ids=" + es.util.url.stringify(ids);
        if (numOfRows) {
            url = url + "&numOfRows=" + numOfRows;
        }
        if (forLayerViews) {
            url = url + "&forLayerViews=" + forLayerViews;
        }
        return this.rqBroker.doGet({url: url, opts: opts});
    }
    
    /**
     * @function es.module.[resource].attributes
     * Get some sample data of the specified LayerMeta IDs
     * @param id layerMetaId
     * @return a jquery deferred object
     */
    attributes(id: number, opts?: ApiOpts): JQuery.Deferred<Attribute[]> {
        if (!ValidateModule.checkId(id)) {
            return es.util.deferred.rejected();
        }
        
        const url = es.api.getURL(this.resourceName) + "/" + id + "/attributes";
        return this.rqBroker.doGet( {url: url, opts: opts} );
    }
    
    /**
     * @function es.module.[resource].editAttributes
     * @param id layerMetaId
     * @param editAttributes
     * @return a jquery deferred object
     */
    editAttributes(id: number, editAttributes: ({removeAttribute: true, name: string} | {name: string, specialFunction: SpecialFunction|undefined})[], opts?: ApiOpts) : JQuery.Deferred<ILayerMeta> {
        if (!ValidateModule.checkId(id) || !ValidateModule.checkMandatoryArray(editAttributes)) {
            return es.util.deferred.rejected();
        }
        
        const url = es.api.getURL(this.resourceName) + "/" + id + "/attributes";
        return this.rqBroker.doPut({
            "url": url,
            "data": editAttributes,
            "opts": opts
        }).done((rs: ILayerMeta) => {
            this.cache.setEntity(this.resourceName, rs);
            this.cache.clear("LayerInfo");
            this.cache.clear("LayerView");
            
            this.eventChannel.fireEvent("onUpdateLayerMeta", rs);
        });
    }
    
    /**
     * @function es.module.[resource].del Remove layer meta
     * @return a jquery deferred object resolved when the request has completed
     */
    remove(id: number, opts?: ApiOpts): JQueryDeferred<boolean> {
        if (!ValidateModule.checkId(id)) {
            return es.util.deferred.rejected();
        }
        
        return this.rqBroker.doDelete({url: es.api.getURL(this.resourceName) + "/" + id, opts: opts}).done((rs: boolean) => {
            this.cache.clear(this.resourceName, id);
            this.cache.clear("LayerInfo");
            this.cache.clear("LayerView");
            
            this.eventChannel.fireEvent("onRemoveLayerMeta", rs);
        });
    }

    /**
     * @function es.module.[resource].del Remove layer meta with dependencies
     * @return a jquery deferred object resolved when the request has completed
     */
    removeWithDependencies(id: number, opts?: ApiOpts): JQuery.Deferred<boolean> {
        if (!ValidateModule.checkId(id)) {
            return es.util.deferred.rejected();
        }

        return this.rqBroker.doDelete({url: es.api.getURL(this.resourceName) + "/" + id + "/with_dependencies", opts: opts}).done((_rs: boolean) => {
            this.cache.clear(this.resourceName, id);
            this.cache.clear("LayerInfo");
            this.cache.clear("LayerView");
            this.cache.clear("GeoMap");
            
            this.eventChannel.fireEvent("onRemoveLayerMeta", id);
        });
    }

    /**
     * Remove layer meta ids with all owned dependencies. Not deleted in case there are layer meta dependencies
     * which can't be removed (not owned).
     * @param ids
     * @param opts
     * @returns {*}
     */
    removeBulkWithDependencies(ids: number[], opts?: ApiOpts) {
        if (!ValidateModule.checkMandatoryArg(ids)) {
            return es.util.deferred.rejected();
        }

        return this.rqBroker.doPost({
            url: es.api.getURL(this.resourceName) + "/remove_bulk_with_dependencies",
            opts: opts,
            data: ids
        }).done(() => {
            this.cache.clear("LayerInfo");
            this.cache.clear("LayerMeta");
            this.cache.clear("LayerView");
            this.cache.clear("GeoMap");
            
            ids.forEach(id => this.eventChannel.fireEvent("onRemoveLayerMeta", id));
        });
    }

    /**
     * Get layer meta ids of layer meta objects which are not used in any map.
     *
     * @param opts
     */
    getOrphans(opts?: ApiOpts) {
       return this.rqBroker.doGet({url: es.api.getURL(this.resourceName) + "/orphans", opts: opts});
    }
    
    /**
     * Given an attribute name (e.g. NAME1) return the linked attribute name that will be generated for it (e.g. ES_LINK_123_NAME1).
     * This method is public as it is useful to be able to query if a layermeta already has a certain linked attribute
     * and, if so, what that attributes name is.
     * @param layerMetaId
     * @param attrName
     */
    getLinkedAttributeName(layerMetaId: number, attrName: string, opts?: ApiOpts) {
        if (!ValidateModule.checkId(layerMetaId)) {
            return es.util.deferred.rejected();
        }
        
        const url = es.api.getURL(this.resourceName) + '/get_linked_attribute_name/' + layerMetaId + "?attrName=" + attrName;
        return this.rqBroker.doGet({'url': url, 'opts': opts});
    }

    /**
     * @function es.module.layermeta.checkTerritoryDependencies
     * Get the territories depencencies for a layerMetaId
     * @return a jquery deferred object resolved when the request has completed
     */
    getTerritoryDependencies(layerMetaId: number, opts?: ApiOpts): JQuery.Deferred<ITerritoryDependency[]> {
        if (!ValidateModule.checkId(layerMetaId)) {
            return es.util.deferred.rejected();
        }
        
        return this.rqBroker.doGet({
            url: es.api.getURL(this.resourceName) + "/territory_dependencies/" + layerMetaId,
            opts: opts
        });
    }

    /**
     * @function es.module.[resource].get
     * Get the resource from the server
     * @return a jquery deferred object resolved when the request has completed
     */
    getAvailabilities(id: number, opts?: ApiOpts): JQuery.Deferred<ILayerMetaAvailabilities> {
        if (!ValidateModule.checkId(id)) {
            return es.util.deferred.rejected();
        }
        
        const url = es.api.getURL(this.resourceName) + "/availability/" + id;
        return this.rqBroker.doGet({url: url, opts: opts});
    }

    getAvailabilitiesFromLayerView(id: number, opts?: ApiOpts): JQuery.Deferred<ILayerMetaAvailabilities|undefined> {
        if (!ValidateModule.checkId(id)) {
            return es.util.deferred.rejected();
        }

        const url = es.api.getURL(this.resourceName) + "/layerview/availability/" + id;
        return this.rqBroker.doGet({url: url, opts: opts});
    }

    getAvailabilitiesByGeoMapId(geoMapId: number, opts?: ApiOpts): JQuery.Deferred<[ILayerMetaAvailabilities]|undefined> {
        if (!ValidateModule.checkId(geoMapId)) {
            return es.util.deferred.rejected();
        }
        const url = es.api.getURL(this.resourceName) + "/geo_map/availability/" + geoMapId;
        return this.rqBroker.doGet({url: url, opts: opts});
    }

    /**
     * @function es.module.[resource].put
     * PUT a new resource to the server (note: id attrib must be valid)
     * @return a jquery deferred object resolved when the request has completed
     */
    createOrUpdateAvailabilities(resourceObj: ILayerMetaAvailabilities, opts?: ApiOpts): JQuery.Deferred<ILayerMeta> {
        if (!ValidateModule.checkMandatoryArg(resourceObj)) {
            return es.util.deferred.rejected();
        }

        return this.rqBroker.doPut({
            url: es.api.getURL(this.resourceName) + "/availability/" + resourceObj.id,
            opts: opts,
            data: resourceObj
        }).done((rs: ILayerMetaAvailabilities) => {
            this.eventChannel.fireEvent("onUpdateLayerMetaAvailabilities", rs);
        });
    }

    /**
     * @function es.module.[resource].del Remove layer meta
     * @return a jquery deferred object resolved when the request has completed
     */
    removeLayerMetaAvailabilities(id: number, opts?: ApiOpts) {
        if (!ValidateModule.checkId(id)) {
            return es.util.deferred.rejected();
        }

        return this.rqBroker.doDelete({
            url: es.api.getURL(this.resourceName) + "/availability/" + id,
            opts: opts
        }).done((rs: boolean) => {
            this.eventChannel.fireEvent("onRemoveLayerMetaAvailabilities", rs);
        });
    }
}
